import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Eye, MousePointerClick, AlertCircle, Clock, Mail, Send } from "lucide-react";

/**
 * Phase 153 — Candidate Email Log
 * Phase 156b — adds a provider ("sent via") column + a lightweight status
 *              filter so recruiters can one-tap narrow down to bounces /
 *              failures and spot deliverability issues at a glance.
 */
const STATUS_META = {
    queued:     { label: "QUEUED",    tone: "bg-neutral-100 text-neutral-700", icon: Clock },
    scheduled:  { label: "SCHEDULED", tone: "bg-amber-100 text-amber-800",     icon: Clock },
    sent:       { label: "SENT",      tone: "bg-blue-100 text-blue-800",       icon: Send },
    delivered:  { label: "DELIVERED", tone: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
    opened:     { label: "OPENED",    tone: "bg-indigo-100 text-indigo-800",   icon: Eye },
    clicked:    { label: "CLICKED",   tone: "bg-fuchsia-100 text-fuchsia-800", icon: MousePointerClick },
    bounced:    { label: "BOUNCED",   tone: "bg-rose-100 text-rose-800",       icon: AlertCircle },
    complained: { label: "COMPLAINT", tone: "bg-rose-100 text-rose-800",       icon: AlertCircle },
    failed:     { label: "FAILED",    tone: "bg-rose-100 text-rose-800",       icon: AlertCircle },
};

/** Human-readable label for each outbound provider the ATS can route through. */
const PROVIDER_META = {
    zeptomail: { label: "ZeptoMail", tone: "border-amber-300 bg-amber-50 text-amber-800" },
    resend:    { label: "Resend",    tone: "border-indigo-300 bg-indigo-50 text-indigo-800" },
    none:      { label: "No provider", tone: "border-slate-300 bg-slate-50 text-slate-600" },
    unknown:   { label: "—",           tone: "border-slate-300 bg-slate-50 text-slate-500" },
};

function ProviderPill({ provider }) {
    const meta = PROVIDER_META[(provider || "unknown").toLowerCase()] || PROVIDER_META.unknown;
    return (
        <span
            className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border rounded ${meta.tone}`}
            data-testid={`email-provider-${provider || "unknown"}`}
            title={`Sent via ${meta.label}`}
        >
            {meta.label}
        </span>
    );
}

export function StatusBadge({ status }) {
    const meta = STATUS_META[status] || STATUS_META.queued;
    const Icon = meta.icon;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${meta.tone}`} data-testid={`email-status-badge-${status}`}>
            <Icon className="w-3 h-3" />
            {meta.label}
        </span>
    );
}

/** Status filter chips. "all" is the pseudo-key for no filter. */
const FILTER_BUCKETS = [
    { key: "all", label: "All" },
    { key: "sent", label: "Sent" },
    { key: "delivered", label: "Delivered" },
    { key: "opened", label: "Opened" },
    { key: "bounced", label: "Bounced" },
    { key: "failed", label: "Failed" },
];

export default function CandidateEmailLog({ candidateId, refreshToken = 0 }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        if (!candidateId) return;
        setLoading(true);
        api.get(`/candidates/${candidateId}/emails`)
            .then((r) => {
                const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.rows || []);
                setRows(list);
            })
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [candidateId, refreshToken]);

    // Client-side counts drive the filter chip labels so the recruiter can see
    // "Bounced · 2" at a glance without opening the chip.
    const counts = useMemo(() => {
        const c = { all: rows.length };
        for (const r of rows) {
            c[r.status] = (c[r.status] || 0) + 1;
        }
        return c;
    }, [rows]);

    const visible = useMemo(() => {
        if (filter === "all") return rows;
        return rows.filter((r) => r.status === filter);
    }, [rows, filter]);

    if (loading) {
        return <div className="text-xs font-mono text-muted-foreground py-4">Loading email history…</div>;
    }
    if (rows.length === 0) {
        return (
            <div className="text-xs text-muted-foreground py-6 text-center border border-dashed border-border rounded-md" data-testid="email-log-empty">
                <Mail className="w-5 h-5 mx-auto mb-2 opacity-50" />
                No emails have been sent to this candidate yet.
            </div>
        );
    }
    return (
        <div className="space-y-2" data-testid="email-log-list">
            {/* Phase 156b — status filter chips. Highlights whichever bucket is
             *  active and shows a tiny count next to each. */}
            <div className="flex items-center gap-1 flex-wrap pb-1" data-testid="email-log-filter-row">
                {FILTER_BUCKETS.map((b) => {
                    const n = counts[b.key] || 0;
                    const active = filter === b.key;
                    const danger = b.key === "bounced" || b.key === "failed";
                    return (
                        <button
                            key={b.key}
                            type="button"
                            onClick={() => setFilter(b.key)}
                            disabled={b.key !== "all" && n === 0}
                            className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border inline-flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                active
                                    ? (danger ? "bg-rose-600 text-white border-rose-600" : "bg-ikb text-white border-ikb")
                                    : "bg-white hover:bg-secondary border-border text-foreground/80"
                            }`}
                            data-testid={`email-log-filter-${b.key}`}
                        >
                            {b.label}
                            {b.key !== "all" && n > 0 && (
                                <span className={`${active ? "text-white/80" : "text-foreground/60"}`}>· {n}</span>
                            )}
                            {b.key === "all" && (
                                <span className={`${active ? "text-white/80" : "text-foreground/60"}`}>· {rows.length}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {visible.length === 0 ? (
                <div className="text-xs text-muted-foreground py-6 text-center border border-dashed border-border rounded-md" data-testid="email-log-filtered-empty">
                    Nothing in this bucket.
                </div>
            ) : visible.map((e) => (
                <div key={e.id} className="border border-border rounded-md p-3 bg-white" data-testid={`email-log-row-${e.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate" title={e.subject}>{e.subject || "(no subject)"}</div>
                            <div className="text-[11px] font-mono text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                                <span>From {e.sent_by_name || "—"}</span>
                                <span>·</span>
                                <span>{new Date(e.created_at).toLocaleString()}</span>
                                {e.template_key && (<><span>·</span><span className="text-ikb">tpl: {e.template_key}</span></>)}
                                {e.scheduled_at && e.status === "scheduled" && (
                                    <><span>·</span><span>Send at {new Date(e.scheduled_at).toLocaleString()}</span></>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            {e.sent_via && e.sent_via !== "unknown" && <ProviderPill provider={e.sent_via} />}
                            <StatusBadge status={e.status} />
                        </div>
                    </div>
                    {e.error && (
                        <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 font-mono" data-testid={`email-log-error-${e.id}`}>{e.error}</div>
                    )}
                    {(e.events && e.events.length > 1) && (
                        <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[10px] font-mono text-muted-foreground">
                            {e.events.slice(-5).map((ev, i) => (
                                <span key={i} className="border border-border rounded px-1.5 py-0.5">
                                    {ev.type?.replace("email.", "")} · {new Date(ev.at).toLocaleTimeString()}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}
